/* eslint-disable max-len */
import { NavigateFunction } from 'react-router-dom';
import { getSubRouteAbsolutePath, RouteGroup, TimeSeriesType, TimeSeriesValueType } from '@moderepo/mode-app-components';
import { fillInRouteParams } from '../routes/RouteHelper';
import { RC_ROUTE_PATHS, RC_SUB_ROUTE_NAME } from './RCProjectConfig';


export class RCRouteHelper {

    private static instance: RCRouteHelper;

    public static getInstance (navigate?: NavigateFunction) {
        // create a new Route Helper instance if we have not create one yet or if we have a 'history' object now but didn't have one before
        if (!RCRouteHelper.instance || (navigate && !RCRouteHelper.instance.navigate)) {
            RCRouteHelper.instance = new RCRouteHelper(navigate);
        }
        return RCRouteHelper.instance;
    }


    private navigate: NavigateFunction | undefined;

    private constructor (navigate?: NavigateFunction) {
        this.navigate = navigate;
    }


    public navigateTo (url: string, replace: boolean = false, delay: number = 0) {
        if (delay > 0) {
            // If there is a delay required, wait 'delay' ms and then call route again with the same params but without delay next time.
            window.setTimeout(() => {
                this.navigateTo(url, replace, 0);
            }, delay);
            return;
        }

        if (this.navigate) {
            this.navigate(url, { replace });
        }
    }


    /**
     * Get the Project's Monitoring/Alert page URL
     */
    public getMonitoringPageUrl (params: {
        readonly projectId: number;
    }): string {
        return fillInRouteParams(RC_ROUTE_PATHS.PROJECTS_$projectId_MONITORING as string, params);
    }

    /**
     * Go to the Project Monitoring/Alerts page
     */
    public gotoMonitoringPage (params: {
        readonly projectId: number;
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getMonitoringPageUrl(params), replace, delay);
    }

    /**
     * Get the Project's Issues page URL
     */
    public getIssuesPageUrl (params: {
        readonly projectId: number;
    }): string {
        return fillInRouteParams(RC_ROUTE_PATHS.PROJECTS_$projectId_ISSUES as string, params);
    }

    /**
     * Go to the Project Issues page
     */
    public gotoIssuesPage (params: {
        readonly projectId: number;
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getIssuesPageUrl(params), replace, delay);
    }


    /**
     * Get the Investigate Issue page URL. There are 3 different route to Investigate page
     *      Without Device Context:                         /projects/12/issues/1234
     *      With Device Context but not User/Home context:  /projects/12/devices/34/issues/1234
     *      With Device/User/Home Context:                  /projects/12/users/34/homes/56/devices/78/issues/1234
     */
    public getInvestigateIssuePageUrl (params: {
        readonly projectId: number;
        readonly issueId: number;
        readonly deviceId?: number;
        readonly userId?: number;
        readonly homeId?: number;
    }) {
        if (params.deviceId === undefined) {
            return `${fillInRouteParams(RC_ROUTE_PATHS.PROJECTS_$projectId_ISSUES_$issueId as string, params)}`;
        }
        if (params.userId && params.homeId) {
            return `${fillInRouteParams(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId_ISSUES_$issueId as string, params)}`;
        }
        if (params.homeId) {
            return `${fillInRouteParams(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId_ISSUES_$issueId as string, params)}`;
        }
        return `${fillInRouteParams(RC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId_ISSUES_$issueId as string, params)}`;
    }

    /**
     * Go to the investigate issue page
     */
    public gotoInvestigateIssuePage (params: {
        readonly projectId: number;
        readonly issueId: number;
        readonly deviceId?: number;
        readonly userId?: number;
        readonly homeId?: number;
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getInvestigateIssuePageUrl(params), replace, delay);
    }


    /**
     * Get the robot listing page
     * @param params
     */
    public getRobotsPageUrl (params: {
        readonly projectId: number;
        readonly userId?: number;
        readonly homeId?: number;
        readonly deviceId?: number;
    }) {
        if (params.userId && params.homeId && params.deviceId) {
            return fillInRouteParams(getSubRouteAbsolutePath(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId as RouteGroup, RC_SUB_ROUTE_NAME.ROBOTS), params);
        }
        if (params.userId && params.homeId) {
            return fillInRouteParams(getSubRouteAbsolutePath(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId as RouteGroup, RC_SUB_ROUTE_NAME.ROBOTS), params);
        }
        if (params.homeId) {
            return fillInRouteParams(getSubRouteAbsolutePath(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId as RouteGroup, RC_SUB_ROUTE_NAME.ROBOTS), params);
        }

        return fillInRouteParams(RC_ROUTE_PATHS.PROJECTS_$projectId_ROBOTS as string, params);
    }

    /**
     * Go to the Robot listing page
     */
    public gotoRobotsPage (params: {
        readonly projectId: number;
        readonly userId?: number;
        readonly homeId?: number;
        readonly deviceId?: number;
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getRobotsPageUrl(params), replace, delay);
    }


    /**
     * Get the Robot's details page url
     */
    public getRobotPageUrl (params: {
        readonly projectId: number;
        readonly homeId: number;
        readonly robotId: string;
        readonly userId?: number | undefined;
        readonly deviceId?: number | null | undefined;
    }) {
        if (params.userId && params.homeId && params.deviceId) {
            // if we have all user/home/device context
            return fillInRouteParams((RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup).rootPath, params);
        }
        if (params.userId && params.homeId) {
            // if we only have user/home context
            return fillInRouteParams((RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_ROBOTS_$robotId as RouteGroup).rootPath, params);
        }

        if (params.homeId && params.deviceId) {
            // if we only have home and device context
            return fillInRouteParams((RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup).rootPath, params);
        }

        if (params.homeId) {
            // if we only have home context
            return fillInRouteParams((RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_ROBOTS_$robotId as RouteGroup).rootPath, params);
        }

        // homeId is required for robot pages so if the route doesn't have homeId in the route params, we have to pass homeId as query params
        const queryParams = `?homeId=${params.homeId}`;

        if (params.deviceId) {
            // if we only have device context
            return `${fillInRouteParams((RC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId_ROBOTS_$robotId as RouteGroup).rootPath, params)}${queryParams}`;
        }

        return `${fillInRouteParams((RC_ROUTE_PATHS.PROJECTS_$projectId_ROBOTS_$robotId as RouteGroup).rootPath, params)}${queryParams}`;
    }

    /**
     * Go to the page that show a robot details
     */
    public gotoRobotPage (params: {
        readonly projectId: number;
        readonly homeId: number;
        readonly robotId: string;
        readonly userId?: number | undefined;
        readonly deviceId?: number | null | undefined;
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getRobotPageUrl(params), replace, delay);
    }



    /**
     * Get the Project's Device Data page URL
     */
    public getDeviceDataPageUrl (params: {
        readonly projectId: number;
        readonly deviceId: number;
        readonly dataId: string;
        readonly userId?: number;
        readonly homeId?: number;
        readonly tsdbSmartModuleId: string;
        readonly timeSeriesType: TimeSeriesType;
        readonly valueType?: TimeSeriesValueType;
        readonly dataDisplayName?: string;
    }): string {
        const query = `?tsdbSmartModuleId=${params.tsdbSmartModuleId}&timeSeriesType=${params.timeSeriesType}&valueType=${params.valueType || TimeSeriesValueType.AGGREGATED}&dataDisplayName=${params.dataDisplayName || ''}`;
        if (params.userId && params.homeId) {
            return `${fillInRouteParams(RC_ROUTE_PATHS.PROJECTS_$projectId_USERS_$userId_HOMES_$homeId_DEVICES_$deviceId_DATA_$dataId as string, params)}${query}`;
        }
        if (params.homeId) {
            return `${fillInRouteParams(RC_ROUTE_PATHS.PROJECTS_$projectId_HOMES_$homeId_DEVICES_$deviceId_DATA_$dataId as string, params)}${query}`;
        }
        return `${fillInRouteParams(RC_ROUTE_PATHS.PROJECTS_$projectId_DEVICES_$deviceId_DATA_$dataId as string, params)}${query}`;
    }

    /**
     * Go to the Project Device Data page
     */
    public gotoDeviceDataPage (params: {
        readonly projectId: number;
        readonly deviceId: number;
        readonly dataId: string;
        readonly userId?: number;
        readonly homeId?: number;
        readonly tsdbSmartModuleId: string;
        readonly timeSeriesType: TimeSeriesType;
        readonly valueType?: TimeSeriesValueType;
        readonly dataDisplayName?: string;
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getDeviceDataPageUrl(params), replace, delay);
    }



    /**
     * Get the Project's Gateway catalog page URL
     */
    public getGatewayCatalogPageUrl (params: {
        readonly projectId: number;
    }): string {
        return fillInRouteParams(RC_ROUTE_PATHS.PROJECTS_$projectId_RC_ADMIN_GATEWAY_CATALOG as string, params);
    }

    /**
     * Go to the Project gateway catalog page
     */
    public gotoGatewayCatalogPage (params: {
        readonly projectId: number;
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getGatewayCatalogPageUrl(params), replace, delay);
    }


    /**
     * Get the Project's Robot catalog page URL
     */
    public getRobotCatalogPageUrl (params: {
        readonly projectId: number;
    }): string {
        return fillInRouteParams(RC_ROUTE_PATHS.PROJECTS_$projectId_RC_ADMIN_ROBOT_CATALOG as string, params);
    }

    /**
     * Go to the Project Robot catalog page
     */
    public gotoRobotCatalogPage (params: {
        readonly projectId: number;
    }, replace: boolean = false, delay: number = 0) {
        this.navigateTo(this.getRobotCatalogPageUrl(params), replace, delay);
    }
}
